<!--
  - Copyright (c) 2023. Haodong JU
  -->

<template>
  <div>
    <ik-data-table
        ref="dataTable"
        :custom-on-row-click="goto"
        :model="model"
        :use-action="false"
        :use-default-action="false"
        entity-name="ItalyXML"
        icon="mdi-cards"
    >
      <template
          #item.sendEmail="{item}"
      >
        <v-btn
            :disabled="item.pecSend !== '0'"
            elevation="0"
            small
            icon
            @click.stop="sendEmail(item)"
        >
          <v-icon>
            mdi-email-fast-outline
          </v-icon>
        </v-btn>
        <v-btn
            elevation="0"
            small
            icon
            @click.stop="showPDFEmailDialog(item)"
        >
          <v-icon>
            mdi-file-pdf-box
          </v-icon>
        </v-btn>
      </template>
    </ik-data-table>
    <v-dialog
      v-model="updateXMLDialog"
      max-width="800px"
      persistent
    >
      <base-form
        :can-edit="editObj.pecSend === '0'"
        :editing-object="editObj"
        :schema="ItalySchemas"
        @close="updateXMLDialog=false"
        @submit="updateXML"
      >
        <div>{{ $t('PrintMenus') }}</div>
        <template #subtitle>
          {{ $t('PrintMenusSubtitle') }}
        </template>
      </base-form>
    </v-dialog>
    <v-dialog
      v-model="inputEmailDialog"
      max-width="800px"
      persistent
    >
      <base-form
        :save-btn-name="'send'"
        :schema="emailSchema"
        :extra-btn="true"
        @close="inputEmailDialog=false"
        @submit="sendPDFEmail"
        @extraBtnFunction="showPDFDialog = true"
      >
        <div>PDF</div>
      </base-form>
    </v-dialog>
    <v-dialog
      v-model="showPDFDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card class="pdf-dialog-card">
        <v-card-text class="pa-0 pdf-container">
          <v-progress-circular
            v-if="isLoading"
            indeterminate
            color="primary"
            size="64"
            class="loading-indicator"
          ></v-progress-circular>
          <iframe
            :src="pdfSrc"
            class="pdf-iframe"
            @load="isLoading = false"
          ></iframe>
        </v-card-text>
        <v-btn
          fab
          color="primary"
          class="close-fab-btn"
          @click="showPDFDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import IkDataTable from '@/plugins/EntityFrameWork/IkDataTable.vue'
import ItalyXML from '@/model/dataLayer/api/Italy/ItalyXML'
import BaseForm from '@/views/widget/INK/BaseForm/BaseForm.vue'
import DateDayPicker from '@/views/widget/commonLogic/DateDayPicker.vue'
import i18n from '@/i18n'
import { sendFPAEmail, sendFPAPDFToEmail, updateFPA } from '@/model/dataLayer/api/Italy/ItalyBills'
import { showSuccessMessage } from '@/model/utils/utils'
import { hillo } from 'hillo'

export default {
  name: 'ItalyXML',
  components: {
    BaseForm,
    IkDataTable
  },
  data () {
    return {
      pdfSrc: '',
      showPDFDialog: false,
      inputEmailDialog: false,
      orderId: '',
      updateXMLDialog: false,
      editObj: {},
      model: ItalyXML,
      isLoading: true
    }
  },
  computed: {
    emailSchema () {
      return [
        {
          key: 'email',
          name: 'Email'
        }
      ]
    },
    ItalySchemas () {
      return [
        {
          key: 'date',
          name: 'Data documento',
          component: DateDayPicker,
          componentProps: {
            locale: i18n.locale
          }
        },
        {
          key: 'pivaPaese',
          name: 'P.IVA Paese',
          componentProps: {
            rules: [v => /^[A-Z]{2}$/.test(v) || '[A-Z]{2}']
          }
        },
        {
          key: 'piva',
          name: 'P.IVA',
          componentProps: {
            rules: [v => /^.{1,28}$/.test(v) || '1-28']
          }
        },
        {
          key: 'codiceFiscale',
          name: 'Codice Fiscale',
          required: false,
          default: '',
          componentProps: {
            rules: [v => {
              if (v !== '') {
                return /^[A-Z0-9]{11,16}$/.test(v) || '[A-Z] [0-9] 11-16'
              }
            }]
          }
        },
        {
          key: 'codiceDestinatario',
          name: 'Codice Amministrazione destinataria',
          default: '0000000',
          componentProps: {
            rules: [v => /^[A-Z0-9]{6,7}$/.test(v) || '[A-Z0-9]{6,7}']
          }
        },
        {
          key: 'pecDestinatario',
          name: 'Destinatario PEC',
          required: false,
          default: '',
          componentProps: {
            rules: [v => {
              if (!v) {
                v = ''
              }
              if (v !== '') {
                return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v) || i18n.t('EnterEmailAddress')
              }
            }]
          }
        },
        {
          key: 'denominazione',
          name: 'Denominazione',
          componentProps: {
            rules: [v => /^[\x20-\xFF]{1,80}$/.test(v) || '1-80']
          }
        },
        {
          key: 'indirizzo',
          name: 'Indirizzo',
          componentProps: {
            rules: [v => /^[\x20-\xFF]{1,60}$/.test(v) || '1-60']
          }
        },
        {
          key: 'cap',
          name: 'CAP',
          componentProps: {
            rules: [v => /^[0-9]{5}$/.test(v) || '[0-9] 5']
          }
        },
        {
          key: 'comune',
          name: 'Comune',
          componentProps: {
            rules: [v => /^[\x20-\xFF]{1,60}$/.test(v) || '1-60']
          }
        },
        {
          key: 'provincia',
          name: 'Provincia',
          componentProps: {
            rules: [v => /^[A-Z]{2}$/.test(v) || '[A-Z]{2}']
          }
        },
        {
          key: 'nazione',
          name: 'Nazione',
          componentProps: {
            rules: [v => /^[A-Z]{2}$/.test(v) || '[A-Z]{2}']
          }
        },
        {
          key: 'causale',
          name: 'Causale',
          required: false,
          default: '',
          componentProps: {
            rules: [v => /^[\x20-\xFF]*$/.test(v) || '']
          }
        }
      ]
    }
  },
  mounted () {
    this.orderId = this.$route.query.orderId
    if (this.orderId) {
      this.$refs.dataTable.search = this.orderId
    }
  },
  methods: {
    async showPDFEmailDialog (item) {
      this.editObj = item
      this.isLoading = true
      this.pdfSrc = hillo.defaults.baseURL + 'FatturaPAController.php?op=previewFPAPDF&id=' + item.id
      this.inputEmailDialog = true
    },
    async sendPDFEmail (info) {
      await sendFPAPDFToEmail(this.editObj.id, info.email)
      showSuccessMessage(i18n.t('submit_success'))
      this.inputEmailDialog = false
      await this.$refs.dataTable.reload()
    },
    async sendEmail (item) {
      await sendFPAEmail(item.id)
      showSuccessMessage(i18n.t('submit_success'))
      await this.$refs.dataTable.reload()
    },
    async updateXML (info) {
      await updateFPA(info)
      this.updateXMLDialog = false
      await this.$refs.dataTable.reload()
    },
    goto (item) {
      this.editObj = item
      this.updateXMLDialog = true
    }
  }
}
</script>

<style scoped>
.pdf-dialog-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.pdf-container {
  position: relative;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  height: calc(100vh - 48px); /* Subtract toolbar height */
  overflow: hidden;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.pdf-toolbar {
  min-height: 48px !important;
  padding: 0 16px !important;
}

.close-fab-btn {
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 10;
}
</style>
